import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import router from "../router/index.js";

/***
 * STORE MODULOS
 * */
 import modSeguridad from "./modSeguridad/index";

import axios from "axios";
import { isDemoUser, maskDemoData } from "../utils/demoMask";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.VUE_APP_API_URL;
Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [createPersistedState()],
  state: {
    user: null,
    errors: null,
    mensaje_login: null,
    auth: false,
    permissions: [],
    menus: [],
    routes: [],
    accessToken: "",
    darkMode: false,
  },
  getters: {
    isAuthenticated: (state) => {
      return state.auth;
    },

    getUser: (state) => {
      return state.user || null;
    },

    getErrors: (state) => {
      return state.errors;
    },

    getPermissions: (state) => {
      return state.permissions;
    },

    getMensajeLogin: (state) => {
      return state.mensaje_login;
    },

    can: (state) => (permissionName) => {
      return (
        state.permissions.find((per) => per.name === permissionName) !==
        undefined
      );
    },

    getRoutes: (state) => {
      return state.routes;
    },

    getMenus: (state) => {
      return state.menus;
    },

    getDarkMode: (state) => {
      return state.darkMode;
    },

    isDemoUser: (state) => {
      return state.user && state.user.id === 78;
    },
  },
  mutations: {
    SET_USER(state, user) {
      if (user && isDemoUser(user)) {
        maskDemoData(user, user);
      }
      state.user = user;
      state.auth = Boolean(user);
    },

    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions;
    },

    SET_ERRORS(state, error) {
      state.errors = error;
    },

    SET_MENUS(state, menus) {
      state.menus = menus;
    },

    SET_ROUTES(state, routes) {
      state.routes = routes;
    },

    SET_DARK_MODE(state, darkMode) {
      state.darkMode = darkMode;
    },
    
    SET_MENSAJE_LOGIN(state, mensaje) {
      state.mensaje_login = mensaje;
    },
  },
  actions: {
    async login({ dispatch, commit }, credentials) {
      await axios.get("/sanctum/csrf-cookie");
      await axios
        .post("/login", credentials)
        .then(async (res) => {
          await dispatch("setDarkMode", false);
          // getUser ahora carga automáticamente permissions, menus y routes
          await dispatch("getUser");
        })
        .catch(async (error) => {
          let res = await Object.assign({}, error);
          let err_pss_date = res.response.data.errors.password_updated_at;
          let err_recaptcha = res.response.data.errors.recaptcha;
          let err_status = res.response.data.errors.status;
          let err_estado_empresa = res.response.data.errors.estado_empresa;
          let err_email = res.response.data.errors.email;
          let err_destiempo = res.response.data.errors.destiempo;
          if (typeof err_pss_date !== "undefined") {
            let errors = "password_updated_at";
            commit("SET_ERRORS", errors);
          }
          if (typeof err_recaptcha !== "undefined") {
            let errors = "recaptcha";
            commit("SET_ERRORS", errors);
          }
          if (typeof err_status !== "undefined") {
            let errors = "status";
            commit("SET_ERRORS", errors);
          }
          if (typeof err_estado_empresa !== "undefined") {
            let errors = "estado_empresa";
            commit("SET_ERRORS", errors);
          }
          if (typeof err_email !== "undefined") {
            let errors = "email";
            let mensaje = res.response.data.errors.email[0];
            commit("SET_ERRORS", errors);
            commit("SET_MENSAJE_LOGIN", mensaje);
          }
          if (typeof err_destiempo !== "undefined") {
            let errors = "destiempo";
            commit("SET_ERRORS", errors);
          }
        });
    },

    async logout({ commit, dispatch }) {
      try {
        await axios.get("/logout");
      } catch (error) {
        // Continuar incluso si el backend falla
      }
      
      // Limpiar estado de Vuex completamente
      commit("SET_USER", null);
      commit("SET_PERMISSIONS", []);
      commit("SET_MENUS", []);
      commit("SET_ROUTES", []);
      commit("SET_ERRORS", null);
      commit("SET_MENSAJE_LOGIN", null);
      
      // Limpiar localStorage completamente
      localStorage.clear();
      sessionStorage.clear();
      
      // Redirigir al login solo si no estamos ya en la página de login
      if (router.currentRoute.path !== '/login') {
        router.push('/login').catch(() => {
          // Manejar error de navegación duplicada silenciosamente
        });
      }
      
      return true;
    },

    async getUser({ commit, dispatch }) {
      try {
        const res = await axios.get("/api/user");
        commit("SET_USER", res.data);
        
        // Solo si el usuario se carga exitosamente, cargar permissions y menus
        await Promise.all([
          dispatch("getPermissions"),
          dispatch("getMenus")
        ]);
        
        // Solo después cargar las rutas
        await dispatch("getRoutes", this.getters.getMenus);
        await dispatch("setRoutes");
        
      } catch (err) {
        commit("SET_USER", null);
        commit("SET_PERMISSIONS", []);
        commit("SET_MENUS", []);
        commit("SET_ROUTES", []);
        
        // Re-lanzar el error para que el caller pueda manejarlo
        throw err;
      }
    },

    async getPermissions({ commit }) {
      await axios
        .get("/api/user/permissions")
        .then((res) => {
          commit("SET_PERMISSIONS", res.data);
        })
        .catch((err) => {
          commit("SET_PERMISSIONS", []);
        });
    },

    async getMenus({ commit }) {
      await axios
        .get("/api/menu")
        .then((response) => {
          commit("SET_MENUS", response.data);
        })
        .catch((err) => {
          commit("SET_MENUS", []);
        });
    },

    async getRoutes({ commit }, menus) {
      var r = [];
      menus.forEach((menu) => {
        menu.nivel2.forEach((nivel2) => {
          nivel2.rutas.forEach((ruta) => {
            r.push(ruta);
          });
        });
      });
      commit("SET_ROUTES", r);
    },

    async setRoutes() {
      this.getters.getRoutes.forEach((dataRoutes) => {
        var newRoute = {
          path: dataRoutes.path,
          component: () => import("../views/" + dataRoutes.component + ".vue"),
          name: dataRoutes.name,
        };
        router.addRoutes([newRoute]);
      });
    },

    async setDarkMode({ commit }, darkMode) {
      commit("SET_DARK_MODE", darkMode);
    },
  },
  modules: {
    modSeguridad: modSeguridad,
  },
});
